export const formatCurrency = (amount, { fractionDigits = 2 } = {}) =>
  new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD',
    minimumFractionDigits: fractionDigits,
    maximumFractionDigits: fractionDigits,
  }).format(Number(amount) || 0);

/** Local calendar YYYY-MM-DD (avoids UTC off-by-one from toISOString). */
export const formatLocalDateYmd = (value) => {
  if (!value) return null;
  const date = value instanceof Date ? value : new Date(value);
  if (Number.isNaN(date.getTime())) return null;
  const y = date.getFullYear();
  const m = String(date.getMonth() + 1).padStart(2, '0');
  const d = String(date.getDate()).padStart(2, '0');
  return `${y}-${m}-${d}`;
};

export const getTechnicianDisplayName = (tech) => {
  if (!tech) return '';
  if (tech.displayName) return tech.displayName;
  if (tech.type === 'vendor') return tech.companyName || tech.company_name || '';
  return `${tech.firstName || tech.first_name || ''} ${tech.lastName || tech.last_name || ''}`.trim();
};

export const panelSurfaceSx = (theme) => {
  const isDark = theme.palette.mode === 'dark';
  return {
    bgcolor: 'background.paper',
    border: '1px solid',
    borderColor: isDark ? '#334155' : '#e7e7e7',
    borderRadius: '10px',
    overflow: 'hidden',
  };
};

export const JOB_KPI_METRIC_LABELS = {
  created: 'Jobs Created',
  completed: 'Job Completed',
  invoiced: 'Jobs Invoiced',
  invoiced_or_loss: 'Jobs Invoiced / Invoiced-loss',
  average_job_size: 'Average Job size',
  invoiced_paid: 'Total Paid',
  invoiced_unpaid: 'Total Unpaid',
};

const SHARED_FILTERS =
  'Also limited by the selected date range on this tab (when set). Open-ended ranges are allowed.';

/** User-facing filter explanations for client Earnings KPIs. */
export const CLIENT_EARNINGS_SUMMARY_INFO = {
  created:
    `Counts every job for this client whose create date falls in the selected range. Includes every status, including Cancelled. ${SHARED_FILTERS}`,
  completed:
    `Counts this client's jobs whose current status is Recall No Charge, Invoiced, Cancelled, or Invoiced/Loss, and whose completion date falls in the selected range. Completion date = invoice date for Invoiced / Invoiced-Loss; lock date for Recall No Charge / Cancelled. ${SHARED_FILTERS}`,
  invoiced:
    `Counts this client's jobs currently Invoiced whose proposal invoice date falls in the selected range. ${SHARED_FILTERS}`,
  invoiced_or_loss:
    `Counts this client's jobs currently Invoiced or Invoiced/Loss whose proposal invoice date falls in the selected range. ${SHARED_FILTERS}`,
  average_job_size:
    `Average NTE of the same jobs as Job Completed (same statuses and completion-date rules). Cancelled / Recall No Charge are included (NTE is often $0). Not based on proposal price. ${SHARED_FILTERS}`,
  invoiced_or_loss_jobs:
    `Revenue, cost, and profit for the same jobs as the Jobs Invoiced / Invoiced-loss count (Invoiced or Invoiced/Loss, dated by invoice date). ${SHARED_FILTERS}`,
  completed_jobs:
    `Revenue, cost, and profit for the same jobs as Job Completed (hybrid completion date; includes Cancelled / Recall No Charge). ${SHARED_FILTERS}`,
  revenue:
    'Sum of NTE for jobs in this group’s universe. Not proposal price; not the paid amount.',
  cost:
    'Sum of all technician costs plus all parts costs on those jobs (no paid-date or parts-approval filter).',
  profits: 'Revenue minus Cost for this group.',
};

export const CLIENT_EARNINGS_STATUS_INFO =
  'Shows pipeline jobs for this client. Counts are not limited by the date range. Includes statuses from Open through Job Done, plus Ready for Invoicing. Cancelled, Invoiced, Invoiced/Loss, and Recall No Charge are not in this set.';

export const CLIENT_EARNINGS_PAID_INFO = {
  section:
    'Invoiced & Invoiced/Loss jobs only. Uses the same invoice-date range as Jobs Invoiced / Invoiced-loss. Totals are sum of NTE split by the job Paid flag.',
  paid:
    'Sum of NTE for this client’s Invoiced and Invoiced/Loss jobs marked Paid, with proposal invoice date in the selected range. Click to list those jobs.',
  unpaid:
    'Sum of NTE for this client’s Invoiced and Invoiced/Loss jobs not marked Paid, with proposal invoice date in the selected range. Click to list those jobs.',
};

export const formatEarningsRangeLabel = (dateStart, dateEnd) => {
  const fmt = (d) => {
    if (!d) return null;
    const date = d instanceof Date ? d : new Date(d);
    if (Number.isNaN(date.getTime())) return null;
    return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
  };
  const start = fmt(dateStart);
  const end = fmt(dateEnd);
  if (start && end) return `${start} – ${end}`;
  if (start) return `From ${start}`;
  if (end) return `Through ${end}`;
  return 'All time';
};
